export enum ApplicationApiUrls {
  Applications = "/v1/Applications",
  ApplicationSign = "/v1/Applications/Sign",
  ApplicationNumber = "/v1/Applications/SequenceNumber",
  ApplicationTypes = "/v1/Applications/Types",
  ApplicationState = "/v1/Applications/:id/State",
  AccountOpening = "/v1/AccountOpeningApplication",
  AccountClosure = "/v1/AccountClosureApplication",
  PhoneBanking = "/v1/PhoneBankingApplication",
  PayrollCardOpening = "/v1/PayrollCardOpeningApplication",
  PeriodicalPayments = "/v1/PeriodicalPaymentsApplication",
  CardServiceInstructionApplication = "/v1/CardServiceInstructionApplication",
  BankBranch = "/v1/BankInfrastructure/Branches",
  UserOpeningApplication = "/v1/UserOpeningApplication",
  LoanTrancheDisbursementApplication = "/v1/LoanTrancheDisbursementApplication",
  DepositTerminationApplication = "/v1/DepositTerminationApplication",
}

export enum Paths {
  Applications = "applications",
  ApplicationHistory = "history/application",
  AccountOpeningApplication = "account/opening",
  AccountOpeningApplicationUpdate = "account/opening/:operationId/update",
  AccountOpeningApplicationCopy = "account/opening/:operationId/copy",
  AccountClosureApplication = "account/closure",
  AccountClosureApplicationUpdate = "account/closure/:operationId/update",
  AccountClosureApplicationCopy = "account/closure/:operationId/copy",
  PhoneBankingApplication = "phone-banking",
  PhoneBankingApplicationUpdate = "phone-banking/:operationId/update",
  PhoneBankingApplicationCopy = "phone-banking/:operationId/copy",
  PayrollCardOpeningApplication = "payroll-card-opening",
  PayrollCardOpeningApplicationUpdate = "payroll-card-opening/:operationId/update",
  PayrollCardOpeningApplicationCopy = "payroll-card-opening/:operationId/copy",
  LoanTrancheDisbursementApplication = "loan-tranche-disbursement",
  LoanTrancheDisbursementApplicationUpdate = "loan-tranche-disbursement/:operationId/update",
  LoanTrancheDisbursementApplicationCopy = "loan-tranche-disbursement/:operationId/copy",
  CardServiceInstructionApplication = "card-service-instruction",
  CardServiceInstructionApplicationUpdate = "card-service-instruction/:operationId/update",
  CardServiceInstructionApplicationCopy = "card-service-instruction/:operationId/copy",
  PeriodicalPaymentsApplication = "periodical-payments",
  PeriodicalPaymentsApplicationUpdate = "periodical-payments/:operationId/update",
  PeriodicalPaymentsApplicationCopy = "periodical-payments/:operationId/copy",
  UserOpeningApplication = "user-opening",
  UserOpeningApplicationUpdate = "user-opening/:operationId/update",
  UserOpeningApplicationCopy = "user-opening/:operationId/copy",
  DepositTerminationApplication = "deposit-termination",
  DepositTerminationApplicationUpdate = "deposit-termination/:operationId/update",
  DepositTerminationApplicationCopy = "deposit-termination/:operationId/copy",
}

export enum ApplicationKeys {
  ApplicationNumber = "applicationNumber",
  PhoneNumber = "phoneNumber",
  CreditCardNumber = "creditCardNumber",
  CreditAccount = "creditAccount",
  Purpose = "purpose",
  LoanContractNumber = "loanContractNumber",
  Roles = "roles",
  Currency = "currency",
  DocumentFile = "documentFile",
  Beneficiary = "beneficiary",
}

export enum ApplicationTypes {
  AccountOpeningApplication = "AccountOpeningApplication",
  AccountClosureApplication = "AccountClosureApplication",
  PhoneBankingApplication = "PhoneBankingApplication",
  PayrollCardOpeningApplication = "PayrollCardOpeningApplication",
  CardServiceInstructionApplication = "CardServiceInstructionApplication",
  UserOpeningApplication = "UserOpeningApplication",
  LoanTrancheDisbursementApplication = "LoanTrancheDisbursementApplication",
  PeriodicalPaymentsApplication = "PeriodicalPaymentsApplication",
  DepositTerminationApplication = "DepositTerminationApplication",
}

export enum ApplicationApiTagTypes {
  ApplicationHistory = "ApplicationHistory",
}

export enum PhoneBankingService {
  ReceiveInformation = "ReceiveInformation",
  DefineExchangeRate = "DefineExchangeRate",
}

export enum InstructionActions {
  SMSNotificationActivation = "SMSNotificationActivation",
  CardUnblockOrActivation = "CardUnblockOrActivation",
  CardBlocking = "CardBlocking",
  PasswordChange = "PasswordChange",
  InstantActivationOfAmount = "InstantActivationOfAmount",
  CardReissuance = "CardReissuance",
  CardInsurance = "CardInsurance",
}

export enum ApplicationFeatureToggles {
  AccountOpeningApplication = "isAccountOpeningApplicationAvailable",
  AccountClosureApplication = "isAccountClosureApplicationAvailable",
  PhoneBankingApplication = "isPhoneBankingApplicationAvailable",
  PayrollCardOpeningApplication = "isPayrollCardOpeningApplicationAvailable",
  CardServiceInstructionApplication = "isPaymentCardServiceInstructionApplicationAvailable",
  LoanTrancheDisbursementApplication = "isLoanTrancheDisbursementApplicationAvailable",
  UserOpeningApplication = "isUserOpeningApplicationAvailable",
  PeriodicalPaymentsApplication = "isPeriodicalPaymentsApplicationAvailable",
  DepositTerminationApplication = "isDepositTerminationApplicationAvailable",
}

export enum CardReissuanceReason {
  PinCodeChange = "PinCodeChange",
  CardRetention = "CardRetention",
  ReissuanceBeforeValidityPeriod = "ReissuanceBeforeValidityPeriod",
  CardDamage = "CardDamage",
  CardLossOrFraud = "CardLossOrFraud",
  CardTypeChange = "CardTypeChange",
}

export enum ReissuedCardReceivingType {
  Delivery = "Delivery",
  BankBranch = "BankBranch",
}

export enum Roles {
  Viewer = "Viewer",
  Editor = "Editor",
  Signer1 = "Signer1",
  Signer2 = "Signer2",
}

export enum WorkingDayAdjustment {
  PrevWorkingDay = "PrevWorkingDay",
  NoneWorkingDay = "NoneWorkingDay",
  NextWorkingDay = "NextWorkingDay",
}

export enum FinancialProduct {
  Main = "main",
  Loan = "loan",
  Other = "other",
}

export const GroupedApplicationsTypes: Record<FinancialProduct, ApplicationTypes[]> = {
  [FinancialProduct.Main]: [
    ApplicationTypes.AccountOpeningApplication,
    ApplicationTypes.AccountClosureApplication,
    ApplicationTypes.PayrollCardOpeningApplication,
    ApplicationTypes.CardServiceInstructionApplication,
    ApplicationTypes.PeriodicalPaymentsApplication,
    ApplicationTypes.DepositTerminationApplication,
  ],
  [FinancialProduct.Loan]: [ApplicationTypes.LoanTrancheDisbursementApplication],
  [FinancialProduct.Other]: [
    ApplicationTypes.PhoneBankingApplication,
    ApplicationTypes.UserOpeningApplication
  ],
};

export enum BeneficiaryBank {
  Other = "Other",
  Acba = "Acba",
  Ararat = "Ararat",
  Ameria = "Ameria",
  Amio = "Amio",
  IDBank = "IDBank",
  Ardshin = "Ardshin",
  Armswiss = "Armswiss",
  Artsakh = "Artsakh",
  Byblos = "Byblos",
  Evoca = "Evoca",
  Ineco = "Ineco",
  Converse = "Converse",
  Aeb = "Aeb",
  Mellat = "Mella",
  Unibank = "Unibank",
  VTB = "VTB",
  Fast = "Fast",
  Gov = "Gov",
}
